import { PriceList, IPriceList } from "./PriceList";
import { PriceGroup } from "./PriceGroup";
import { PriceGroupOption, IPriceGroupOption } from "./PriceGroupOption";
import { CustomerUser } from "./CustomerUser";
import { Price } from "./Price";
import { ProductStatus } from "./Product";
import { IStringDictionary, StringDictionary } from "../../core/StringDictionary";

export enum PriceCodeStatus {
    Active = 0,
    Retired = 1
}

export class PriceGroupCode implements IPriceGroupCode {
    static getFormattedPriceCode(priceGroupId: number, priceId: number, version: string): string {
        return priceGroupId.toString() + '-' + priceId.toString() + '-' + version;
    }
    private versionChars = "ABCDEFGHJKLMNPQRTUVWXYZ";
    static mapChars(): string { return "0123456789ABCDEFGHJKMNPQRTUVWXYZ"; }
    static checkChars(): string { return "ABCDEFGHJKMNPQRTUVWXYZ"; }

    constructor(priceGroupCode?: IPriceGroupCode) {
        // initialize typed object from generic object
        this.version = priceGroupCode?.version ?? "AF";
        this.priceList = priceGroupCode?.priceList instanceof PriceList ? priceGroupCode.priceList : new PriceList(priceGroupCode?.priceList);
        this.customerCount = priceGroupCode?.customerCount ?? 0;
        this.priceGroupOptions = priceGroupCode?.priceGroupOptions ? PriceGroupOption.toTypedArray(priceGroupCode.priceGroupOptions) : undefined;
        this.dictionary = priceGroupCode?.dictionary instanceof StringDictionary ? priceGroupCode.dictionary : new StringDictionary(priceGroupCode?.dictionary);
        this.priceId = priceGroupCode?.priceId ?? 15;
        this.priceGroupTypeCode = priceGroupCode?.priceGroupTypeCode ?? "General";
        this.status = priceGroupCode?.status ?? PriceCodeStatus.Active;
        this.alternatePriceCode = priceGroupCode?.alternatePriceCode ?? "";
        this.priceCode = priceGroupCode?.priceCode ?? "0-15-AF";
        this.priceGroupId = priceGroupCode?.priceGroupId ?? 0;
    }

    static getNewPriceGroupCode(priceCode?: string): PriceGroupCode {
        if (!priceCode || priceCode === "")
            throw new Error("Empty price code not allowed");

        let priceGroupCode = new PriceGroupCode();
        priceGroupCode.priceCode = priceCode;

        let parts = priceCode.split('-');
        if (parts.length !== 3)
            throw new Error("PriceCode must be in canonical form: 9999-9999-XX");

        priceGroupCode.priceGroupId = Number(parts[0]);
        if (priceGroupCode.priceGroupId === Number.NaN)
            throw new Error("PriceCode did not contain an integer price group.");

        priceGroupCode.priceId = Number(parts[1]);
        if (priceGroupCode.priceId === Number.NaN)
            throw new Error("PriceCode did not contain an integer price list.");

        if (parts[2].length != 2 || (parts[2][0] < 'A' && parts[2][0] > 'Z'))
            throw new Error("PriceCode did not contain a version or check character.");
        priceGroupCode.version = parts[2][0];

        let code = PriceGroupCode.getFormattedPriceCode(priceGroupCode.priceGroupId, priceGroupCode.priceId, priceGroupCode.version);
        let luhnChar = this.luhnChar(code, PriceGroupCode.mapChars(), PriceGroupCode.checkChars());
        if (parts[2][1] != luhnChar)
            throw new Error("PriceCode did not contain a valid check character. Expected " + luhnChar + " but got " + parts[2][1]);

        return priceGroupCode;
    }

    toJSON(): object {
        // return generic object with properties to stringify
        return {
            version: this.version,
            priceList: this.priceList,
            customerCount: this.customerCount,
            priceGroupOptions: this.priceGroupOptions,
            dictionary: this.dictionary,
            priceId: this.priceId,
            priceGroupTypeCode: this.priceGroupTypeCode,
            status: this.status,
            alternatePriceCode: this.alternatePriceCode,
            priceCode: this.priceCode,
            priceGroupId: this.priceGroupId,
        };
    }

    private _priceCode?: string;
    get priceCode(): string {
        if (this._priceCode !== undefined)
            return this._priceCode;
        let code = PriceGroupCode.getFormattedPriceCode(this.priceGroupId, this.priceId, this.version);
        return code + PriceGroupCode.luhnChar(code, PriceGroupCode.mapChars(), PriceGroupCode.checkChars());
    }
    set priceCode(value: string | undefined) {
        this._priceCode = value;
    }

    alternatePriceCode: string;
    status: PriceCodeStatus;

    private _priceGroupId?: number;
    get priceGroupId(): number {
        if (this._priceGroup !== undefined)
            this._priceGroupId = this._priceGroup.priceGroupId;
        return this._priceGroupId ?? 0;
    }
    set priceGroupId(value: number) {
        this._priceGroupId = value;
    }

    priceGroupTypeCode: string;
    priceId: number;
    version: string;
    priceList?: PriceList | undefined;
    customerCount: number;
    priceGroupOptions: PriceGroupOption[] | undefined;
    dictionary: StringDictionary;

    static luhnChar(input: string, map: string, check: string) : string {
        let sum = 0;
        let multiplier = 2;

        input = input.replace("-", "").replace("-", "");
        for (let i = input.length - 1; i >= 0; i--)
        {
            const addend = map.indexOf(input[i])*multiplier;
            sum += ~~(addend/map.length) + (addend%map.length);     // ~~ forces the value to be an int
            multiplier = multiplier === 2 ? 1 : 2;
        }

        return check[(map.length - sum%map.length)%check.length];
    }

    findPriceGroupOption(productNumber: string, unitCode: string, throwNotFound = true) {
        let foundPriceGroupOption = null;
        if (this.priceGroupOptions) {
            foundPriceGroupOption = this.priceGroupOptions.find(p => { return p.productNumber.localeCompare(productNumber) === 0 && (!unitCode || p.unitCode.localeCompare(unitCode) === 0) });
        }
        if (foundPriceGroupOption == null && throwNotFound)
            throw new Error(`"${productNumber}" does not exist in PriceGroupOptions.`);

        return foundPriceGroupOption;
    }

    getEffectivePrices(user: CustomerUser | undefined = undefined): Price[] {
        const list = [] as Price[];
        if (this.priceList)
            //C# code does a sort on price group options, but without a comparer, so I'm not sure it's actually effective or necessary
            this.priceGroupOptions?.forEach(option => {
                const price = this.priceList?.findPrice(option.productNumber, option.unitCode, false);
                if (price?.product && price.product.status === ProductStatus.Active &&
                    (!price.product.meritItemId ||
                        user && user.meritItems.indexOf(price.product.meritItemId) > -1))
                    list.push(price);
            });
        return list;
    }

    update(priceCode: PriceGroupCode) {
        this.priceCode = priceCode.priceCode;
        this.status = priceCode.status;
        this.priceId = priceCode.priceId;
        this.version = priceCode.version;
    }

    // Calculated properties

    private _priceGroup?: PriceGroup;

    get enabled(): boolean {
        return this.status == PriceCodeStatus.Active;
    }

    get isRetail(): boolean {
        return this.priceGroupTypeCode === "General";
    }

    get newPriceCode(): string {
        let code = PriceGroupCode.getFormattedPriceCode(this.priceGroupId, this.priceId, this.newVersion);
        return code + PriceGroupCode.luhnChar(code, PriceGroupCode.mapChars(), PriceGroupCode.checkChars());
    }

    get newVersion(): string {
        if (this.version === "" || this.versionChars.indexOf(this.version) < 0)
            return this.versionChars[0];
        return this.versionChars[(this.versionChars.indexOf(this.version) + 1) % this.versionChars.length];
    }

    get allVisible(): boolean {
        return this.priceGroupOptions?.every(o => o.visible) ?? false;
    }
    set allVisible(value: boolean) {
        if (this.priceGroupOptions)
            this.priceGroupOptions.forEach(o => o.visible = value);
    }
}

export interface IPriceGroupCode {
    version: string;
    priceList?: IPriceList;
    customerCount: number;
    priceGroupOptions: IPriceGroupOption[] | undefined;
    dictionary: IStringDictionary;
    priceId: number;
    priceGroupTypeCode: string;
    status: PriceCodeStatus;
    alternatePriceCode: string;
    priceCode: string;
    priceGroupId: number;
}